import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';

import { filesAPIService } from '~/api/files';
import { pdfMicrositeAPIService } from '~/api/microsites/pdf';
import { CustomDropZone } from '~/components/atoms/CustomDropZone';
import { UploadedImagePreview } from '~/components/atoms/UploadedImagePreview';
import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import Checkbox from '~/components/ui/Checkbox';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { getIsMobile } from '~/store/reducers/app/selectors';
import { pdfSlice } from '~/store/reducers/previewScreens/pdf';
import { updatePdfFileThunk, updatePdfThunk } from '~/store/reducers/previewScreens/pdf';
import { getPdfReducer } from '~/store/reducers/previewScreens/pdf/selectors';
import { COLORS } from '~/styles/colors';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

import { ImageItem } from '../sub-components/ImageItem';
import { pdfForms } from '../utils';

const { actions } = pdfSlice;

export default function BasicInformation() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    micrositeData,
    pdfInformation,
    selectedTemplate,
    basicInformation: { coverImage, images, isFileUpload },
  } = useSelector(getPdfReducer);
  const isMobile = useSelector(getIsMobile);

  const addCoverImage = async (event) => {
    const file = event.target.files[0];
    const value = window.URL.createObjectURL(file);
    const storageResponse = await filesAPIService.uploadImage(file);

    dispatch(actions.setIsCoverImageUploaded(true));
    dispatch(
      actions.setBasicInformation({ field: 'coverImage', value: { img: value, imageStorageId: storageResponse.data } }),
    );
    dispatch(updatePdfThunk({ cover_image_id: storageResponse.data }));
  };

  const setIsDirectlyShowThePdf = (bool) => {
    dispatch(actions.setBasicInformation({ field: 'isFileUpload', value: bool }));
    dispatch(updatePdfThunk({ direct_pdf_link: bool }));
  };

  const handleSendField = (field, value) => {
    dispatch(updatePdfThunk({ [field]: value }));
  };
  const handleDropFiles = async (val) => {
    let uniquePdfList = val.map((item) => {
      return { ...item, isLoading: true };
    });

    dispatch(actions.setImages([...images, ...uniquePdfList]));

    for (const [index, item] of val.entries()) {
      try {
        const res = await pdfMicrositeAPIService.uploadSinglePdfFile({
          name: item.name,
          file_id: item.imageStorageId,
          size: item.size,
          microsite: micrositeData?.id,
          position: images.length === 0 ? index : index + images.length,
          description: '',
        });

        if (res?.data?.id) {
          uniquePdfList = uniquePdfList.map((pdfItem, currentIndex) =>
            currentIndex === index ? { ...pdfItem, unique_id: res?.data?.id, isLoading: false } : pdfItem,
          );

          const newImages = [...images, ...uniquePdfList];
          if (isFileUpload && newImages.length !== 1) {
            setIsDirectlyShowThePdf(false);
          }

          dispatch(actions.setImages(newImages));
        }
      } catch (error) {
        logger.error(`Error uploading PDF at index ${index}:`, error);
      }
    }
  };

  const removePdf = async (id) => {
    const newImages = images.filter((item) => item?.unique_id !== id);

    pdfMicrositeAPIService.deletePdfFiles(id);

    if (isFileUpload && newImages.length !== 1) {
      setIsDirectlyShowThePdf(false);
    }

    dispatch(actions.setImages(newImages));
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(images, result.source.index, result.destination.index);
    const pdfsConverted = items.map(async (item, index) => {
      await dispatch(
        updatePdfFileThunk({
          id: micrositeData.id,
          body: {
            data: { position: index },
            id: item.unique_id,
          },
        }),
      );
    });
    await Promise.all(pdfsConverted);
    const response = await pdfMicrositeAPIService.getPdfSite(micrositeData.id);
    dispatch(actions.setMicrositeData(response.data));
    dispatch(actions.setImages(items));
  };

  const removeCoverImage = () => {
    dispatch(actions.setBasicInformation({ field: 'coverImage', value: '' }));
    dispatch(updatePdfThunk({ cover_image_id: null }));
  };

  return (
    <CreateQRContentBlockLayout type="basicInfo">
      <CustomDropZone
        maxSizeInKb={100000}
        onDrop={handleDropFiles}
        maxSize={100}
        fileTypes="PDF"
        existedLength={images.length}
        acceptTypes="application/pdf"
      />

      {images.length === 0 && (
        <Typography
          className="Mui-error MuiFormHelperText-root"
          sx={{
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: COLORS.darkRed,
            marginTop: '8px',
          }}
        >
          <ErrorOutlineRoundedIcon sx={{ fontSize: '16px' }} />
          {t('template1:error')}
        </Typography>
      )}
      {!selectedTemplate &&
        images?.map((item) => {
          return (
            <Box key={item?.unique_id}>
              <UploadedImagePreview
                id={item.unique_id}
                imageName={item?.name}
                imageSize={item?.size}
                deleteHandler={removePdf}
                isLoading={item?.isLoading}
                isPDF
              />
            </Box>
          );
        })}
      {!!selectedTemplate && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                {images?.map((item, index, array) => {
                  return <ImageItem key={item?.id} item={item} index={index} actions={actions} images={array} />;
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {(!images?.length || images.length < 2) && (
        <Checkbox
          label={t('template1:uploaderDesc')}
          onChange={(e) => setIsDirectlyShowThePdf(e.target.checked)}
          checked={isFileUpload}
          sx={{ marginTop: '12px', marginBottom: '6px' }}
        />
      )}
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('template1:pdfInfo')}
        details={
          <>
            {selectedTemplate >= 3 && (
              <Box sx={{ margin: '12px 0' }}>
                <Text variant="inputSmall" sx={{ marginBottom: '8px' }}>
                  {t('template5:coverImage')}
                </Text>
                <ImageUploader image={coverImage.img} onDeleteImage={removeCoverImage} onUploadImage={addCoverImage} />
              </Box>
            )}
            <Box
              sx={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row', marginBottom: '12px' }}
            >
              <TextInput
                onChange={(e) => dispatch(actions.setPdfInformation({ field: pdfForms[0].id, value: e.target.value }))}
                disabled={isFileUpload}
                onBlur={() => {
                  handleSendField(pdfForms[0].id, pdfInformation[pdfForms[0].id]);
                }}
                fullWidth
                label={t(pdfForms[0].label)}
                placeholder={t(pdfForms[0].placeholder)}
                inputProps={{ maxLength: pdfForms[0].maxLength }}
                helperText={isMaxLength(pdfInformation[pdfForms[0].id], pdfForms[0].maxLength)}
                defaultValue={pdfInformation[pdfForms[0].id]}
              />
              <TextInput
                disabled={isFileUpload}
                onChange={(e) => dispatch(actions.setPdfInformation({ field: pdfForms[1].id, value: e.target.value }))}
                onBlur={() => {
                  handleSendField(pdfForms[1].id, pdfInformation[pdfForms[1].id]);
                }}
                fullWidth
                label={t(pdfForms[1].label)}
                placeholder={t(pdfForms[1].placeholder)}
                inputProps={{ maxLength: pdfForms[1].maxLength }}
                helperText={isMaxLength(pdfInformation[pdfForms[1].id], pdfForms[1].maxLength)}
                defaultValue={pdfInformation[pdfForms[1].id]}
              />
            </Box>

            <TextInput
              disabled={isFileUpload}
              onChange={(e) => dispatch(actions.setPdfInformation({ field: pdfForms[2].id, value: e.target.value }))}
              onBlur={() => {
                handleSendField(pdfForms[2].id, pdfInformation[pdfForms[2].id]);
              }}
              sx={{ marginBottom: '12px' }}
              fullWidth
              label={t(pdfForms[2].label)}
              placeholder={t(pdfForms[2].placeholder)}
              inputProps={{ maxLength: pdfForms[2].maxLength }}
              helperText={isMaxLength(pdfInformation[pdfForms[2].id], pdfForms[2].maxLength)}
              defaultValue={pdfInformation[pdfForms[2].id]}
            />

            <Box sx={{ display: 'flex', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
              <UrlTextField
                requireProtocol={false}
                urlValue={pdfInformation[pdfForms[3].id]}
                disabled={isFileUpload}
                required={false}
                onChange={(e) => {
                  dispatch(actions.setPdfInformation({ field: pdfForms[3].id, value: e.target.value }));
                }}
                label={t(pdfForms[3].label)}
                inputProps={{ maxLength: pdfForms[3].maxLength }}
                helperText={isMaxLength(pdfInformation[pdfForms[3].id], pdfForms[3].maxLength)}
                onBlur={({ isValid }) => isValid && handleSendField(pdfForms[3].id, pdfInformation[pdfForms[3].id])}
              />
              {selectedTemplate === 0 && (
                <TextInput
                  disabled={isFileUpload}
                  onChange={(e) =>
                    dispatch(actions.setPdfInformation({ field: pdfForms[4].id, value: e.target.value }))
                  }
                  onBlur={() => {
                    handleSendField(pdfForms[4].id, pdfInformation[pdfForms[4].id]);
                  }}
                  fullWidth
                  label={t(pdfForms[4].label)}
                  placeholder={t(pdfForms[4].placeholder)}
                  inputProps={{ maxLength: pdfForms[4].maxLength }}
                  helperText={isMaxLength(pdfInformation[pdfForms[4].id], pdfForms[4].maxLength)}
                  defaultValue={pdfInformation[pdfForms[4].id]}
                />
              )}
            </Box>
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
